import type React from "react"
import styled, { css } from "styled-components"
import type { FontAwesomeIcon } from "~/ur-react-components/FontAwesomeIcon"
import { Icon, type IconProps } from "~/ur-react-components/Icon"
import type { CSSButtonType } from "~/ur-react-components/types/style"

interface BaseButtonProps {
  width?: string
  size?: "small" | "medium"
  variant?: "primary" | "secondary" | "tertiary"
}

const BaseButton = styled.button<BaseButtonProps>`
  width: ${(props) => props.width ?? "auto"};

  // The design uses inner borders, so we have to adjust the padding accordingly
  padding: 12px 24px;
  font-size: 14px;
  
  border-radius: 8px;

  outline: 0;
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;

  // Gap between icon and text
  gap: 10px;

  outline: none;
  
  cursor: pointer;

  box-sizing: border-box;

  /* VARIANT: PRIMARY (Default) */
  ${(props) =>
    (props.variant === "primary" || !props.variant) &&
    css`
      background: ${(props) => props.theme.colors.primary40};
      border: 2px solid transparent;
      color: white;

      &:hover {
        box-shadow: 0px 1px 2px rgba(0, 0, 0, 0.3),
          0px 1px 3px 1px rgba(0, 0, 0, 0.15);
        background: ${(props) => props.theme.colors.primary50};
        border: 2px solid ${(props) => props.theme.colors.primary50};
      }

      &:focus {
        background: ${(props) => props.theme.colors.primary50};
        border: 2px solid ${(props) => props.theme.colors.primary50};
      }

      &:active {
        background: ${(props) => props.theme.colors.primary50};
        border: 2px solid ${(props) => props.theme.colors.primary30};
      }

      &:disabled {
        background: ${(props) => props.theme.colors.gray95};
        border: 2px solid ${(props) => props.theme.colors.gray95};
        box-shadow: none;
        color: ${(props) => props.theme.colors.gray60};
        cursor: not-allowed;
      }
    `}

  /* VARIANT: SECONDARY (White/Gray) */
  ${(props) =>
    props.variant === "secondary" &&
    css`
      background: white;
      border: 1px solid ${(props) => props.theme.colors.gray80};
      color: ${(props) => props.theme.colors.gray20};
      box-shadow: 0px 1px 2px rgba(0, 0, 0, 0.05);

      &:hover {
        background: ${(props) => props.theme.colors.gray95};
        border-color: ${(props) => props.theme.colors.gray60};
        color: ${(props) => props.theme.colors.gray10};
      }

      &:focus {
        background: white;
        border-color: ${(props) => props.theme.colors.primary40};
        box-shadow: 0 0 0 3px ${(props) => props.theme.colors.primary10};
      }

      &:active {
        background: ${(props) => props.theme.colors.gray90};
      }

      &:disabled {
        background: ${(props) => props.theme.colors.gray95};
        border-color: ${(props) => props.theme.colors.gray90};
        color: ${(props) => props.theme.colors.gray60};
        cursor: not-allowed;
        box-shadow: none;
      }
      
      /* Make icons follow text color */
      svg {
        fill: currentColor;
      }
    `}

  /* VARIANT: TERTIARY (Transparent) */
  ${(props) =>
    props.variant === "tertiary" &&
    css`
      background: transparent;
      border: 2px solid transparent;
      color: ${(props) => props.theme.colors.gray40};
      box-shadow: none;
      padding: 6px 12px; /* Less padding for tertiary */

      &:hover {
        background: ${(props) => props.theme.colors.gray95};
        color: ${(props) => props.theme.colors.gray20};
      }

      &:active {
        background: ${(props) => props.theme.colors.gray90};
      }
      
      &:disabled {
        color: ${(props) => props.theme.colors.gray80};
        background: transparent;
        cursor: not-allowed;
      }
    `}

  ${(props) =>
    props.size === "small" &&
    css`
      padding: 6px 16px;
      font-size: 13px;
      gap: 6px;
    `}
`

const InnerText = styled.span`
  font-size: inherit;
  line-height: 1.5;
  font-weight: 500;
  letter-spacing: 0.01em;
  display: inline-flex;
  align-items: center;
  justify-content: center;
`

export interface ButtonProps {
  width?: string
  size?: "small" | "medium"
  variant?: "primary" | "secondary" | "tertiary"
  leftIcon?: FontAwesomeIcon
  rightIcon?: FontAwesomeIcon

  leftIconProps?: IconProps
  rightIconProps?: IconProps

  onClick?: () => void

  disabled?: boolean

  type?: CSSButtonType
  
  // HTML title attribute
  title?: string
}

export const Button: React.CFC<ButtonProps> = ({
  width,
  size = "medium",
  variant = "primary",
  children,
  leftIcon,
  rightIcon,
  leftIconProps,
  rightIconProps,
  onClick,
  disabled,
  type,
  title,
}) => {
  return (
    <BaseButton
      width={width}
      onClick={onClick}
      disabled={disabled}
      size={size}
      variant={variant}
      type={type}
      title={title}
    >
      {leftIcon && <Icon icon={leftIcon} {...leftIconProps} />}
      <InnerText>{children}</InnerText>
      {rightIcon && <Icon icon={rightIcon} {...rightIconProps} />}
    </BaseButton>
  )
}
